import React from "react";
import { WorkPatternRow } from "../../../generated/core-api";
import { calendarInfoForCycleDay } from "./workPatternCycleCalendar";
import styles from "./WorkPatternCycleGrid.module.css";

interface WorkPatternCycleGridProps {
  pattern: WorkPatternRow;
  /**
   * When true, any day cell whose hours exceed 24 is rendered with an error
   * highlight so the user can see at a glance which day caused the validation
   * failure. Used by the clustered report for `dayHours` error rows.
   */
  markOverflowDays?: boolean;
  /**
   * When true, the pattern name and cycle metadata header above the grid is
   * hidden. Use when the parent already displays that information (e.g. the
   * clustered report accordion shows name + metadata above the grid itself).
   */
  hideHeader?: boolean;
  /**
   * When true, renders without the outer card chrome — for nested use inside
   * the clustered report accordion (#426 design sign-off).
   */
  embedded?: boolean;
}

/**
 * Day-by-day cycle read-back for a single work pattern (wireframe WKP-04).
 *
 * Renders each day of the pattern's `dayHours` as a labelled cell so a
 * reviewer can confirm the imported pattern matches what they expect —
 * satisfying AC-01 / R3 ("readable identically to manually created patterns").
 *
 * Rendered purely from the client's already-parsed pattern data; no backend
 * round-trip is required.
 */
/** Preview layout: seven day cells per row (calendar-style grouping). */
const DAYS_PER_ROW = 7;

/** Convert ISO YYYY-MM-DD to DD/MM/YYYY for display. Passes through unchanged if not ISO. */
function formatDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

function chunkPreviewRows(dayHours: number[]): number[][] {
  const rows: number[][] = [];
  for (let i = 0; i < dayHours.length; i += DAYS_PER_ROW) {
    rows.push(dayHours.slice(i, i + DAYS_PER_ROW));
  }
  return rows;
}

export function WorkPatternCycleGrid({
  pattern,
  markOverflowDays,
  hideHeader,
  embedded,
}: WorkPatternCycleGridProps) {
  const scopeCode = pattern.workPatternCode ?? "";
  return (
    <div
      className={embedded ? styles.gridEmbedded : styles.gridCard}
      data-testid="cycle-grid-root"
      data-embedded={embedded ? "true" : "false"}
    >
      {!hideHeader && (
        <div className={styles.gridHeader}>
          <span className={styles.patternName}>{pattern.workPatternName}</span>
          <span className={styles.meta}>
            Employee Group {scopeCode} · {pattern.cycleDays}-day cycle
            from {formatDate(pattern.startDate)}
          </span>
        </div>
      )}

      <div className={styles.grid}>
        {chunkPreviewRows(pattern.dayHours).map((rowHours, rowIndex) => (
          <div key={rowIndex} className={styles.previewRow}>
            <span className={styles.rowLabel}>Row {rowIndex + 1}</span>
            <div className={styles.rowDays}>
              {rowHours.map((hours, offset) => {
                const index = rowIndex * DAYS_PER_ROW + offset;
                const { dow, date } = calendarInfoForCycleDay(pattern.startDate, index);
                const isOverflow = markOverflowDays && hours > 24;
                return (
                  <div
                    key={index}
                    className={`${styles.day} ${isOverflow ? styles.bad : hours > 0 ? styles.on : styles.off}`}
                  >
                    <span className={styles.cycleDay}>Day {index + 1}</span>
                    <span className={styles.dayLabel}>{dow}</span>
                    <span className={styles.dayDate}>{date}</span>
                    <span className={styles.dayHours}>{hours}h</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
